import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useHistory, useLocation } from 'react-router-dom';
import { navigateToQuoteForm } from '../utils/quoteNavigation';
import { trackEvent } from '../utils/analytics';
import { business, colors, layout } from '../theme/tokens';

/**
 * Desktop: a single WhatsApp button bottom-right.
 * Mobile: one sticky action bar (call / WhatsApp / quote) instead of several floating buttons.
 * The mobile bar hides while the quote form itself is on screen.
 */
const FloatingActions = () => {
  const history = useHistory();
  const location = useLocation();
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    const target = document.getElementById('quote-form');
    if (!target) {
      setFormVisible(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setFormVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [location.pathname]);

  return (
    <>
      {/* Desktop WhatsApp */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'fixed', right: 24, bottom: 24, zIndex: 1300 }}>
        <Tooltip title="Chat on WhatsApp" placement="left">
          <IconButton
            component="a"
            href={business.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat with us on WhatsApp"
            sx={{
              width: 56,
              height: 56,
              backgroundColor: colors.whatsapp,
              color: colors.white,
              boxShadow: '0 10px 24px rgba(0,0,0,0.4)',
              transition: 'transform .2s ease, background-color .2s ease',
              '&:hover': { backgroundColor: '#1fbf5b', transform: 'translateY(-2px)' },
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Mobile action bar */}
      <Box
        component="nav"
        aria-label="Quick contact"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          height: layout.mobileBarHeight,
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          pb: 'env(safe-area-inset-bottom)',
          backgroundColor: 'rgba(17,17,17,0.92)',
          backdropFilter: 'blur(14px)',
          borderTop: `1px solid ${colors.border}`,
          transform: formVisible ? 'translateY(110%)' : 'translateY(0)',
          transition: 'transform .3s ease',
        }}
      >
        <IconButton
          component="a"
          href={business.phoneHref}
          aria-label={`Call ${business.phoneDisplay}`}
          sx={{ border: `1px solid ${colors.borderStrong}`, width: 46, height: 46 }}
        >
          <PhoneIcon />
        </IconButton>
        <IconButton
          component="a"
          href={business.whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          sx={{ backgroundColor: colors.whatsapp, color: colors.white, width: 46, height: 46, '&:hover': { backgroundColor: '#1fbf5b' } }}
        >
          <WhatsAppIcon />
        </IconButton>
        <Button
          variant="contained"
          fullWidth
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            trackEvent('quote_cta_click', { cta_label: 'mobile_bar' });
            navigateToQuoteForm(history, location);
          }}
          sx={{ height: 46 }}
        >
          Get a free quote
        </Button>
      </Box>
      {/* Reserve space so the bar never covers footer content on mobile */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, height: layout.mobileBarHeight }} />
    </>
  );
};

export default FloatingActions;
